
import React from 'react';
import { GitCompare, CheckCircle, XCircle, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export function ProductComparison() {
  const comparison = {
    products: [
      {
        id: 1,
        name: 'ASUS ROG Strix RTX 4080',
        price: 1199,
        rating: 4.8,
        image: '/images/ASUS-ROG-Strix-GeForce-RTX-4080.jpg',
        url: 'https://www.amazon.com/dp/B0BLFTWYLZ',
        features: {
          'Memory': '16GB GDDR6X',
          'Base Clock': '2205 MHz',
          'Boost Clock': '2505 MHz',
          'Ray Tracing': true,
          'DLSS 3': true,
          'Power Draw': '320W',
        }
      },
      {
        id: 2,
        name: 'MSI Gaming X Trio RTX 4080',
        price: 1249,
        rating: 4.7,
        image: '/images/MSI-Gaming-GeForce-RTX-4080.jpg',
        url: 'https://www.amazon.com/dp/B0BL668N1X',
        features: {
          'Memory': '16GB GDDR6X',
          'Base Clock': '2230 MHz',
          'Boost Clock': '2520 MHz',
          'Ray Tracing': true,
          'DLSS 3': true,
          'Power Draw': '330W',
        }
      },
    ],
    categories: ['Memory', 'Base Clock', 'Boost Clock', 'Ray Tracing', 'DLSS 3', 'Power Draw']
  };

  const getBestValue = (feature, products) => {
    if (typeof products[0].features[feature] === 'boolean') return null;
    if (feature === 'Power Draw') {
      return Math.min(...products.map(p => parseInt(p.features[feature])));
    }
    return Math.max(...products.map(p => parseInt(p.features[feature])));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700"
    >
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-4 mb-6 text-center sm:text-left">
        {/* Left section */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="bg-purple-500/20 p-3 rounded-xl">
            <GitCompare className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Product Comparison</h2>
            <p className="text-slate-400 text-sm sm:text-base">Compare specs side by side</p>
          </div>
        </div>

        {/* Right section (button) */}
        <div className="w-full sm:w-auto">
          <Link
            to="/compare"
            title="Compare now"
            className="line-bg w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white text-sm sm:text-base font-semibold py-2 px-4 rounded-lg transition-colors flex justify-center items-center gap-2"
          >
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span>Compare Now</span>
          </Link>
        </div>
      </div>


      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-600">
              <th className="text-left py-4 px-4 text-slate-400 font-medium">Feature</th>
              {comparison.products.map((product, index) => {
                const affiliateLink = `${product.url}?tag=dtoplist-20`;
                return (
                <motion.th
                  key={product.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="text-center py-4 px-4 min-w-[200px]"
                >
                  <a
                    href={affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:no-underline"
                  >
                    <div className="bg-slate-700/50 rounded-xl border border-slate-600/50">
                      <div className="rounded-t-xl bg-white p-4">
                        <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-48 object-contain mb-4"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-semibold text-sm mb-2">{product.name}</h3>
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-slate-300 text-sm">{product.rating}</span>
                        </div>
                        <div className="text-blue-400 font-bold text-lg">${product.price}</div>
                      </div>
                    </div>
                  </a>
                </motion.th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {comparison.categories.map((feature, featureIndex) => {
              const bestValue = getBestValue(feature, comparison.products);
              
              return (
                <motion.tr
                  key={feature}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: featureIndex * 0.1 }}
                  className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors"
                >
                  <td className="py-4 px-4 text-slate-300 font-medium">{feature}</td>
                  {comparison.products.map((product) => {
                    const value = product.features[feature];
                    const isBoolean = typeof value === 'boolean';
                    const isBest = !isBoolean && (
                      (feature === 'Power Draw' && parseInt(value) === bestValue) ||
                      (feature !== 'Power Draw' && parseInt(value) === bestValue)
                    );
                    
                    return (
                      <td key={`${product.id}-${feature}`} className="py-4 px-4 text-center">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${
                          isBest ? 'bg-green-500/20 text-green-400' : 
                          isBoolean ? (value ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400') :
                          'bg-slate-600/50 text-slate-300'
                        }`}>
                          {isBoolean ? (
                            value ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />
                          ) : (
                            isBest && <CheckCircle className="w-4 h-4" />
                          )}
                          <span className="text-sm font-medium">
                            {isBoolean ? (value ? 'Yes' : 'No') : value}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.section>
  );
}
